import { useEffect, useState } from "react";
import { loadLocalizedContentWithFallback } from "./faqs";

export interface TestimonialItem {
  id?: string;
  Name: string;
  Location?: string | null;
  Comment: string;
  Active: boolean;
}

export type TestimonialLoadState =
  | {
      status: "loading";
      testimonials: TestimonialItem[];
    }
  | {
      status: "ready";
      testimonials: TestimonialItem[];
    }
  | {
      status: "unavailable";
      testimonials: TestimonialItem[];
    };

function normalizeTestimonialItem(raw: unknown): TestimonialItem | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const record = raw as Record<string, unknown>;
  const name = typeof record.Name === "string" ? record.Name.trim() : "";
  const comment =
    typeof record.Comment === "string" ? record.Comment.trim() : "";

  if (!name || !comment) {
    return null;
  }

  const id =
    typeof record.id === "string" && record.id.trim()
      ? record.id.trim()
      : undefined;
  const location =
    typeof record.Location === "string" && record.Location.trim()
      ? record.Location.trim()
      : undefined;

  return {
    id,
    Name: name,
    Location: location,
    Comment: comment,
    Active: record.Active === true,
  };
}

function normalizeTestimonialItems(raw: unknown): TestimonialItem[] {
  const items = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object" && Array.isArray((raw as { items?: unknown }).items)
      ? ((raw as { items: unknown[] }).items)
      : [];

  return items
    .map(normalizeTestimonialItem)
    .filter((item): item is TestimonialItem => item !== null);
}

export async function loadTestimonials(
  language?: string | null,
  signal?: AbortSignal,
): Promise<TestimonialItem[]> {
  return loadLocalizedContentWithFallback(
    "testimonials",
    normalizeTestimonialItems,
    language,
    signal,
  );
}

export async function loadTestimonialsWithFallback(
  language?: string | null,
  signal?: AbortSignal,
): Promise<TestimonialItem[]> {
  return loadTestimonials(language, signal);
}

export function useTestimonials(language?: string | null): TestimonialLoadState {
  const [state, setState] = useState<TestimonialLoadState>({
    status: "loading",
    testimonials: [],
  });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setState({
      status: "loading",
      testimonials: [],
    });

    const run = async () => {
      try {
        const testimonials = await loadTestimonials(
          language ?? undefined,
          controller.signal,
        );

        if (!active || controller.signal.aborted) {
          return;
        }

        setState({
          status: "ready",
          testimonials,
        });
      } catch {
        if (!active || controller.signal.aborted) {
          return;
        }

        setState({
          status: "unavailable",
          testimonials: [],
        });
      }
    };

    void run();

    return () => {
      active = false;
      controller.abort();
    };
  }, [language]);

  return state;
}
